import * as React from "react";
import { Route } from "react-router";
import { EventEmitter } from "fbemitter";

import { EntityType, Sensor, Key, SensorDraft, GeotabSensor } from "../../models";

import { LabelStore } from "../flux/Label/LabelStore";
import { FlagStore } from "../flux/Flag/FlagStore";
import { ErrorStore } from "../flux/Error/ErrorStore";
import { ListStore } from "../flux/Grid/ListStore";
import { SelectedItemsStore } from "../flux/Grid/SelectedItemsStore";
import { DeleteObjectsStore } from "../flux/DeleteObjects";
import { DateRangeStore } from "../components/Pages/Sensor/DateRangeStore";
import { ValueStore } from "../flux/Value/ValueStore";
import { SearchWithInitiatorCommand } from "../components/Pages/commands/EntitySearchCommand";
import { DeleteObjectsCommand } from "../components/Pages/commands/DeleteObjects";
import { UpsertCommand } from "../components/Pages/commands/UpsertCommand";
import { UpsertSensorCommand } from "../components/Pages/Sensor/commands/UpsertSensorCommand";
import { ShowSensorsDataCommand } from "../components/Pages/SensorsData/commands/ShowSensorsDataCommand";
import { LoadSensorsDataCommand } from "../components/Pages/Sensor/commands/LoadSensorsDataCommand";
import { Polyglot } from "../../polyglot";
import { DeleteObjectsFn } from "../../rest/DeleteObjects";
import { Command, Command1, Command2 } from "../../Command";
import { SearchFn } from "../../rest/Search";
import { CommitFn } from "../../rest/Commit";
import { GoogleMapLoader } from "../utils/GoogleMapLoader";
import { GetSensorsDataFn, SensorsDataResult, GetGeotabSensorsFn } from "../../rest/Geotab";
import { EditSensorStore } from "../components/Pages/Sensor/flux";
import { SensorTab } from "../components/Pages/Sensor/SensorRoute";
import { GetMeasuresFn } from "../../rest/Misc/index";
import { SetVehicleCommand } from "../components/Pages/Sensor/commands/Vehicle/SetVehicleCommand";
import { LoadGeotabSensorsCommand } from "../components/Pages/Sensor/commands/Sensor/LoadGeotabSensorsCommand";
import { SetGeotabSensorCommand } from "../components/Pages/Sensor/commands/Sensor/SetGeotabSensorCommand";
import { InitUpsertSensorCommand } from "../components/Pages/Sensor/commands/InitUpsertSensorCommand";
import { LoadVehiclesCommand } from "../components/Pages/Sensor/commands/Vehicle/LoadVehiclesCommand";
import { LoadMeasuresCommand } from "../components/Pages/Sensor/commands/LoadMeasuresCommand";
import { routeFor } from "../routes";
import { SearchProps } from "../components/Pages/SearchProps";
import { TabSearchConfigurator } from "./configureSearchBox";

// This class is a configuration for Sensors tab
// Constructor receives and/or creates dependencies and injects them
export class SensorTabConfigurator
{
    private readonly editStore = new EditSensorStore();
    private readonly listStore = new ListStore<Sensor>(s => s.id, EntityType.Sensor);
    private readonly selectedItemsStore = new SelectedItemsStore(this.listStore, EntityType.Sensor);
    private readonly toDeleteItemsStore = new DeleteObjectsStore(EntityType.Sensor);
    private readonly loadingVehicles = new FlagStore();
    private readonly loadingGeotabSensors = new FlagStore();
    private readonly loadingQuantities = new FlagStore();

    private readonly upsertInProgressStore = new FlagStore();
    private readonly upsertErrorStore = new ErrorStore();
    private readonly toolbarErrorStore = new ErrorStore();
    private readonly dateRangeStore = new DateRangeStore();
    private readonly loadSensorsDataInProgressStore = new FlagStore();
    private readonly alertStore = new FlagStore();
    private readonly vehicleSearchInitiatorStore = new ValueStore<Key>();
    
    private readonly deleteCmd: Command;
    private readonly searchCmd: Command1<any>;    
    private readonly internalUpsertCmd: Command2<any, EntityType>;
    private readonly upsertCmd: Command1<SensorDraft>;
    private readonly sensorsDataShowCmd: Command1<SensorsDataResult>;
    private readonly loadSensrosDataCmd: Command1<string>;
    
    private readonly initUpsertSensorCmd: Command1<Key>;
    private readonly setVehicleCmd: Command1<Key>;
    private readonly loadVehiclesCmd: Command;
    private readonly setGeotabSensorCmd: Command1<GeotabSensor>;
    private readonly loadGeotabSensorsCmd: Command1<Key>;
    private readonly loadMeasuresCmd: Command;
    
    readonly tableTitleStore = new LabelStore();    
    readonly searchVehicleCmd: SearchWithInitiatorCommand;

    readonly markup: JSX.Element;
    readonly searchConfig: TabSearchConfigurator;

    constructor(private polyglot: Polyglot, private runSearch: SearchFn, commitEntity: CommitFn, deleteObjects: DeleteObjectsFn, getSensorsData: GetSensorsDataFn, getGeotabSensors: GetGeotabSensorsFn, getMeasures: GetMeasuresFn, getCurrentDivisionId: () => number, divisionEmitter: EventEmitter, googleMapLoader: GoogleMapLoader)
    {
        this.searchConfig = new TabSearchConfigurator(polyglot, EntityType.Sensor, getCurrentDivisionId, divisionEmitter, this.tableTitleStore.dispatchToken);
        this.deleteCmd = new DeleteObjectsCommand(EntityType.Sensor, this.toDeleteItemsStore, deleteObjects);
        this.internalUpsertCmd = new UpsertCommand(commitEntity, this.listStore.dispatchToken, this.upsertInProgressStore.dispatchToken, this.upsertErrorStore.dispatchToken);
        this.upsertCmd = new UpsertSensorCommand(polyglot, this.upsertErrorStore.dispatchToken, this.internalUpsertCmd);
        this.sensorsDataShowCmd = new ShowSensorsDataCommand(googleMapLoader, this.alertStore.dispatchToken);
        this.loadSensrosDataCmd = new LoadSensorsDataCommand(getSensorsData, () => [...this.selectedItemsStore.getState().selected], this.sensorsDataShowCmd, this.loadSensorsDataInProgressStore.dispatchToken, this.toolbarErrorStore.dispatchToken);

        this.searchVehicleCmd = new SearchWithInitiatorCommand(polyglot, EntityType.Vehicle, runSearch, this.vehicleSearchInitiatorStore.dispatchToken, this.toolbarErrorStore.dispatchToken);
        this.setGeotabSensorCmd = new SetGeotabSensorCommand(this.editStore.getState);
        this.loadGeotabSensorsCmd = new LoadGeotabSensorsCommand(getGeotabSensors, this.editStore.getState, this.setGeotabSensorCmd, this.loadingGeotabSensors.dispatchToken, this.upsertErrorStore.dispatchToken);
        this.setVehicleCmd = new SetVehicleCommand(this.loadGeotabSensorsCmd, this.setGeotabSensorCmd);
        this.loadVehiclesCmd = new LoadVehiclesCommand(runSearch, this.editStore.getState, this.setVehicleCmd, this.loadingVehicles.dispatchToken, this.upsertErrorStore.dispatchToken);
        this.loadMeasuresCmd = new LoadMeasuresCommand(getMeasures, this.editStore.getState, getCurrentDivisionId, this.loadingQuantities.dispatchToken, this.upsertErrorStore.dispatchToken);

        this.initUpsertSensorCmd = new InitUpsertSensorCommand(this.loadVehiclesCmd, this.loadMeasuresCmd, this.loadGeotabSensorsCmd);

        this.markup = (
            <Route path={routeFor(EntityType.Sensor)} render={() => (
                <SensorTab
                    polyglot={this.polyglot}
                    isAdmin={true}
                    tableTitleStore={this.tableTitleStore}
                    listStore={this.listStore}
                    selectedItemsStore={this.selectedItemsStore}
                    deleteObjectsStore={this.toDeleteItemsStore}
                    deleteObjectsCommand={this.deleteCmd}

                    {...this.searchConfig.searchBoxProps}

                    loadingVehicles={this.loadingVehicles}
                    loadingGeotabSensors={this.loadingGeotabSensors}
                    loadingQuantities={this.loadingQuantities}
                    upsertInProgressStore={this.upsertInProgressStore}
                    upsertErrorStore={this.upsertErrorStore}
                    upsertCommand={this.upsertCmd}

                    vehicleSearchCmd={this.searchVehicleCmd}
                    vehicleSearchInitiatorStore={this.vehicleSearchInitiatorStore}

                    alertVisibilityStore={this.alertStore}
                    toolbarErrorStore={this.toolbarErrorStore}
                    loadSensorsDataCmd={this.loadSensrosDataCmd}
                    loadSensorsDataInProgressStore={this.loadSensorsDataInProgressStore}

                    dateRangeStore={this.dateRangeStore}

                    editStore={this.editStore}
                    upsertInitCmd={this.initUpsertSensorCmd}
                    upsertSetVehicle={this.setVehicleCmd}
                    upsertSetGeotabSensor={this.setGeotabSensorCmd}
                />
            )}/>
        )
    }
}


// WEBPACK FOOTER //
// ./src/app/init/sensor.tsx